import { ReactNode, useCallback, useMemo, useState } from 'react';
import { StandardModal, StandardModalProps } from '../StandardModal/StandardModal';
import { IncompleteStepAlertDetection, WizardStep, WizardStepNavigation } from './constants/types';
import { WizardSteps } from './components/WizardSteps';
import { WizardStepContent } from './components/WizardStepContent';
import { WizardAside } from './components/WizardAside';
import { WizardFooter } from './components/WizardFooter';
import { WizardStepContext, WizardStepContextValue } from './context/WizardStepContext';
import './styles/_wizardModal.scss';

export type { WizardStep, WizardStepNavigation } from './constants/types';
export { IncompleteStepAlertDetection } from './constants/types';
export { useWizardStep } from './context/WizardStepContext';
export type { UseWizardStepOptions, UseWizardStepResult } from './context/WizardStepContext';

export interface WizardModalProps extends StandardModalProps {
    /** Sidebar title shown under the logo (e.g. "Add Loan"). */
    title?: string;
    /** The wizard's steps. Displayed and navigated in order of step.number. */
    steps: WizardStep[];
    /** Number of the current step. The parent owns this so it can persist / restore position. */
    currentStep: number;
    /** Called with the new step's number whenever the wizard moves (Back, Continue, or clicking a step). */
    onStepChange: (stepNumber: number) => void;
    /**
     * Runs before moving forward (e.g. validate / save the current step).
     * Return false (or resolve to false) to stay on the current step.
     */
    onContinue?: (step: WizardStep) => boolean | void | Promise<boolean | void>;
    /** Called when Continue is pressed on the last step (after onContinue). Defaults to onClose. */
    onFinish?: () => void;
    /** Shows a "Save and Exit" action in the aside when provided. */
    onSaveAndExit?: () => void;
    /**
     * Which steps in the sidebar can be clicked (bubble or label) to jump to them:
     * 'previous' (default) for completed steps only, 'all' for any step, 'none' to disable.
     * Jumping does not run onContinue.
     */
    stepNavigation?: WizardStepNavigation;
    /**
     * Which steps in the sidebar show a red warning bubble while their progressBarInfo isn't complete: None (default),
     * Previous (steps before the current one) or All (every step except the current one).
     */
    incompleteStepAlertDetection?: IncompleteStepAlertDetection;
    continueText?: string;
    /** Continue button text on the last step. Defaults to continueText. */
    finishText?: string;
    saveAndExitText?: string;
    /** Extra sidebar content rendered below the step list (e.g. a progress summary). */
    sidebarFooter?: ReactNode;
}

/**
 * Multi-step modal built on StandardModal. The parent supplies the steps (label, header, help text and
 * content for each) and owns the current step number; the wizard renders the step list in the sidebar,
 * the active step in the content area, Back / Continue in the footer, and an optional "Save and Exit".
 */
export const WizardModal = ({
    id,
    isOpen,
    onClose,
    title,
    steps,
    currentStep,
    onStepChange,
    onContinue,
    onFinish,
    onSaveAndExit,
    stepNavigation = 'previous',
    incompleteStepAlertDetection,
    continueText,
    finishText,
    saveAndExitText,
    sidebarFooter,
    ...modalProps
}: WizardModalProps) => {
    const [isBusy, set_isBusy] = useState(false);

    const orderedSteps = useMemo(() => [...steps].sort((a, b) => a.number - b.number), [steps]);
    const currentIndex = orderedSteps.findIndex((step) => step.number === currentStep);
    const activeStep = orderedSteps[currentIndex];

    const isFirstStep = currentIndex <= 0;
    const isLastStep = currentIndex === orderedSteps.length - 1;
    const previousStep = isFirstStep ? undefined : orderedSteps[currentIndex - 1];
    const nextStep = isLastStep ? undefined : orderedSteps[currentIndex + 1];

    // Save & Continue value set by the active step's content via useWizardStep({ enableSaveAndContinue }).
    // Undefined means the content hasn't set one (or doesn't use the hook), so the button is enabled.
    const [continueOverride, set_continueOverride] = useState<boolean>();

    // Clear the override whenever the step changes or the modal opens/closes, so a value set by one step's
    // content never leaks into another step or a later session. Done during render (not in an effect) so it
    // happens before the new step's content runs its effects and sets a fresh value.
    const [overrideScope, set_overrideScope] = useState({ step: currentStep, isOpen });
    if (overrideScope.step !== currentStep || overrideScope.isOpen !== isOpen) {
        set_overrideScope({ step: currentStep, isOpen });
        set_continueOverride(undefined);
    }

    // Continue can't move onto a disabled step (Finish on the last step isn't affected)
    const enableSaveAndContinue = (continueOverride ?? true) && !nextStep?.disabled;

    const setEnableSaveAndContinue = useCallback((enabled: boolean | undefined) => set_continueOverride(enabled), []);

    const stepContext = useMemo<WizardStepContextValue | null>(
        () => (activeStep ? { step: activeStep, setEnableSaveAndContinue } : null),
        [activeStep, setEnableSaveAndContinue]
    );

    const handleBack = () => {
        if (previousStep && !previousStep.disabled) {
            onStepChange(previousStep.number);
        }
    };

    const handleContinue = async () => {
        if (!activeStep || !enableSaveAndContinue) {
            return;
        }

        if (onContinue) {
            set_isBusy(true);
            try {
                const result = await onContinue(activeStep);
                if (result === false) {
                    return;
                }
            } finally {
                set_isBusy(false);
            }
        }

        if (nextStep) {
            onStepChange(nextStep.number);
        } else {
            (onFinish ?? onClose)();
        }
    };

    return (
        <StandardModal
            {...modalProps}
            id={id}
            isOpen={isOpen}
            onClose={onClose}
            sidebarTitle={title}
            // The step's header sits in StandardModal's top bar (beside "Save and Exit"), above the scrolling content
            contentHeader={activeStep?.header}
            contentHelpText={activeStep?.helpText}
            sidebar={
                <>
                    <WizardSteps
                        id={`${id}_steps`}
                        steps={orderedSteps}
                        currentStep={currentStep}
                        onStepClick={isBusy ? undefined : onStepChange}
                        navigation={stepNavigation}
                        incompleteStepAlertDetection={incompleteStepAlertDetection}
                    />
                    {sidebarFooter}
                </>
            }
            aside={
                onSaveAndExit && (
                    <WizardAside
                        id={id}
                        onSaveAndExit={onSaveAndExit}
                        saveAndExitText={saveAndExitText}
                        disabled={isBusy}
                    />
                )
            }
            footer={
                <WizardFooter
                    id={id}
                    showBack={!isFirstStep}
                    onBack={handleBack}
                    onContinue={handleContinue}
                    continueText={isLastStep ? (finishText ?? continueText) : continueText}
                    continueDisabled={!enableSaveAndContinue}
                    disabled={isBusy || !activeStep}
                />
            }
        >
            {activeStep && (
                <WizardStepContext.Provider value={stepContext}>
                    <WizardStepContent step={activeStep} />
                </WizardStepContext.Provider>
            )}
        </StandardModal>
    );
};
